import { DeleteOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { App, Button, Card, Collapse, Divider, Form, Input, Radio, Skeleton, Space, Tabs, Typography, Col, Row } from "antd";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { hostname } from "../hooks/useEndeavour";

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Panel } = Collapse;

function TranslationPage() {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [jsonData, setJsonData] = useState({});
  const [engJsonData, setEngJsonData] = useState({});
  const [jsonString, setJsonString] = useState("{}");
  const [activeKey, setActiveKey] = useState("tree");
  const treeEditorRef = useRef(null);

  const [currentLang, setCurrentLang] = useState("zh");
  const [languages, setLanguages] = useState({
    zh: { name: "繁體", data: {} },
    cn: { name: "简体", data: {} },
    en: { name: "English", data: {} },
    ja: { name: "日本語", data: {} },
    ko: { name: "한국어", data: {} },
  });

  const api = "/pa-alliance/language/translate";
  const query = useQuery({
    queryKey: ["translation"],
    queryFn: () => {
      return axios
        .get(`${hostname}${api}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        })
        .then((res) => {
          return res.data.payload;
        });
    },
  });

  const mutation = useMutation({
    mutationFn: (data) => {
      const response = axios
        .post(`${hostname}${api}`, data, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        })
        .finally(() => {
          setLoading(false);
        });
      return response;
    },
  });

  // base64 decode function
  const base64Decode = (str) => {
    try {
      // 先 base64 decode，再還原成 UTF-8
      const utf8Str = decodeURIComponent(escape(window.atob(str)));
      return JSON.parse(utf8Str);
    } catch (error) {
      console.error("Base64 decode error:", error);
      return {};
    }
  };

  const handleQueryData = () => {
    // Initialize with sample data for each language
    const updatedLanguages = {
      zh: {
        name: "繁體",
        data: base64Decode(query.data["zh_HK.json"]) || {},
      },
      cn: {
        name: "简体",
        data: base64Decode(query.data["zh_CN.json"]) || {},
      },
      en: {
        name: "English",
        data: base64Decode(query.data["en_US.json"]) || {},
      },
      ja: {
        name: "日本語",
        data: base64Decode(query.data["ja_JP.json"]) || {},
      },
      ko: {
        name: "한국어",
        data: base64Decode(query.data["ko_KR.json"]) || {},
      },
      // Add more languages with their respective data
    };

    setLanguages(updatedLanguages);
    setJsonData(updatedLanguages[currentLang].data);
    setEngJsonData(updatedLanguages["en"].data);
    setJsonString(JSON.stringify(updatedLanguages[currentLang].data, null, 2));
    setLanguages(updatedLanguages);
    form.setFieldsValue({
      json: JSON.stringify(updatedLanguages[currentLang].data, null, 2),
    });
  };

  useEffect(() => {
    if (!query.data) {
      return;
    }
    handleQueryData();
  }, [query.data]);

  // Update JSON string when data changes
  const updateJsonString = (newData, lang = currentLang) => {
    try {
      setJsonString(JSON.stringify(newData, null, 2));

      // Update the specific language data
      setLanguages((prevLangs) => ({
        ...prevLangs,
        [lang]: {
          ...prevLangs[lang],
          data: newData,
        },
      }));
    } catch (error) {
      message.error("Invalid JSON format");
    }
  };

  const handleLanguageChange = (lang) => {
    setLanguages((prevLangs) => ({
      ...prevLangs,
      [currentLang]: {
        ...prevLangs[currentLang],
        data: jsonData,
      },
    }));

    // 切換語言
    setCurrentLang(lang);

    // 載入新語言的資料
    const newData = languages[lang]?.data || {};
    setJsonData(newData);
    setJsonString(JSON.stringify(newData, null, 2));

    // Update the raw JSON form field
    const baseFormValues = {
      json: JSON.stringify(newData, null, 2),
    };

    // Create form values for all keys and values in the tree structure
    const formValues = buildFormValues(newData);

    // Update all form fields at once
    form.setFieldsValue({
      ...baseFormValues,
      ...formValues,
    });
  };

  // Helper function to build form values for all keys and values
  const buildFormValues = (data, basePath = []) => {
    const result = {};

    Object.entries(data).forEach(([key, value]) => {
      const currentPath = [...basePath, key];
      const pathStr = currentPath.join(".");

      // Add the key field
      result[`keys.${pathStr}`] = key;

      if (typeof value === "object" && value !== null) {
        // Recursively process nested objects
        const nestedValues = buildFormValues(value, currentPath);
        Object.assign(result, nestedValues);
      } else {
        // Add value field for leaf nodes
        result[`values.${pathStr}`] = String(value);
      }
    });

    return result;
  };

  // Recursively render form fields for JSON properties
  const renderJsonForm = (data, path = []) => {
    return Object.keys(data).map((key) => {
      const currentPath = [...path, key];
      const currentPathStr = currentPath.join(".");
      const keyFieldName = `keys.${currentPathStr}`;
      const valueFieldName = `values.${currentPathStr}`;

      if (typeof data[key] === "object" && data[key] !== null) {
        // Render nested object
        return (
          <Collapse key={currentPathStr} defaultActiveKey={currentPathStr} bordered={false} data-key={currentPathStr}>
            <Panel
              id={`item-${currentPathStr}`}
              header={
                <Space>
                  <Form.Item name={keyFieldName} initialValue={key} noStyle>
                    <Input style={{ width: 150 }} id={`key-input-${currentPathStr}`} onClick={(e) => e.stopPropagation()} />
                  </Form.Item>

                  <Text type="secondary">(Object)</Text>
                  <Button
                    loading={loading}
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteField(currentPath);
                    }}
                  />
                </Space>
              }
              key={currentPathStr}
            >
              <div style={{ paddingLeft: 20, borderLeft: "1px dashed #e5e7eb" }}>
                {renderJsonForm(data[key], currentPath)}
                <Space style={{ width: "100%", marginTop: 8 }}>
                  <Button loading={loading} type="primary" icon={<PlusOutlined />} onClick={() => handleAddField(currentPath)} style={{ flex: 1 }}>
                    New Field
                  </Button>
                  <Button
                    loading={loading}
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddNestedObjectAtPath(currentPath)}
                    style={{ flex: 1 }}
                  >
                    New Object
                  </Button>
                </Space>
              </div>
            </Panel>
          </Collapse>
        );
      } else {
        // Render leaf node (string/number/boolean value)
        return (
          <Form.Item key={currentPathStr} id={`item-${currentPathStr}`}>
            <Space style={{ width: "100%" }}>
              <Form.Item name={keyFieldName} initialValue={key} noStyle>
                <Input id={`key-input-${currentPathStr}`} />
              </Form.Item>
              :
              <Form.Item name={valueFieldName} initialValue={String(data[key])} noStyle>
                <Input />
              </Form.Item>
              <Button loading={loading} icon={<DeleteOutlined />} danger onClick={() => handleDeleteField(currentPath)} />
            </Space>
          </Form.Item>
        );
      }
    });
  };

  const renderEngJsonForm = (data, path = []) => {
    return Object.keys(data).map((key) => {
      const currentPath = [...path, key];
      const currentPathStr = currentPath.join(".");
      const keyFieldName = `keys.${currentPathStr}`;
      const valueFieldName = `values.${currentPathStr}`;

      if (typeof data[key] === "object" && data[key] !== null) {
        // Render nested object
        return (
          <Collapse key={currentPathStr} defaultActiveKey={currentPathStr} bordered={false} data-key={currentPathStr}>
            <Panel
              id={`item-${currentPathStr}`}
              header={
                <Space>
                  <Form.Item name={keyFieldName} initialValue={key} noStyle>
                    <Input style={{ width: 150 }} id={`key-input-${currentPathStr}`} onClick={(e) => e.stopPropagation()} />
                  </Form.Item>

                  <Text type="secondary">(Object)</Text>
                  {/* <Button
                    loading={loading}
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteField(currentPath);
                    }}
                  /> */}
                </Space>
              }
              key={currentPathStr}
            >
              <div style={{ paddingLeft: 20, borderLeft: "1px dashed #e5e7eb" }}>
                {renderEngJsonForm(data[key], currentPath)}
                <Space style={{ width: "100%", marginTop: 8 }}>
                  <Button loading={loading} type="primary" icon={<PlusOutlined />} onClick={() => handleAddField(currentPath)} style={{ flex: 1 }}>
                    New Field
                  </Button>
                  <Button
                    loading={loading}
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleAddNestedObjectAtPath(currentPath)}
                    style={{ flex: 1 }}
                  >
                    New Object
                  </Button>
                </Space>
              </div>
            </Panel>
          </Collapse>
        );
      } else {
        // Render leaf node (string/number/boolean value)
        return (
          <Form.Item key={currentPathStr} id={`item-${currentPathStr}`}>
            <Space style={{ width: "100%" }}>
              <Form.Item name={keyFieldName} initialValue={key} noStyle>
                <Input id={`key-input-${currentPathStr}`} />
              </Form.Item>
              :
              <Form.Item name={valueFieldName} initialValue={String(data[key])} noStyle>
                <Input />
              </Form.Item>
              {/* <Button loading={loading} icon={<DeleteOutlined />} danger onClick={() => handleDeleteField(currentPath)} /> */}
            </Space>
          </Form.Item>
        );
      }
    });
  };

  // Delete a field
  const handleDeleteField = (path) => {
    // 直接複製目前語言的資料
    const newData = JSON.parse(JSON.stringify(jsonData));
    let current = newData;

    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }

    delete current[path[path.length - 1]];
    setJsonData(newData);
    message.success("The Field has been deleted");
  };

  // Add a nested object at the specified path
  const handleAddNestedObjectAtPath = (path) => {
    const newData = JSON.parse(JSON.stringify(jsonData));
    let current = newData;

    for (const key of path) {
      current = current[key];
    }

    // Create a default new field name
    let newFieldName = "NewObject";
    let counter = 1;
    while (current[newFieldName]) {
      newFieldName = `NewObject${counter}`;
      counter++;
    }

    // Add the new nested object with a default child field
    current[newFieldName] = { ChildField: "" };
    // updateJsonString(newData, currentLang);
    setJsonData(newData);
    message.success("New nested object has been added");
  };

  // Add a new field
  const handleAddField = (path) => {
    const newData = JSON.parse(JSON.stringify(jsonData));
    let current = newData;

    for (const key of path) {
      current = current[key];
    }

    // Create a default new field name
    let newFieldName = "NewField";
    let counter = 1;
    // 修正：用 Object.prototype.hasOwnProperty.call 判斷 key 是否存在
    while (Object.prototype.hasOwnProperty.call(current, newFieldName)) {
      newFieldName = `NewField${counter}`;
      counter++;
    }

    // Add the new field
    current[newFieldName] = "";
    setJsonData(newData);
    message.success("New field has been added");
  };

  // Add a root level field
  const handleAddRootField = () => {
    const newData = JSON.parse(JSON.stringify(jsonData));

    let newFieldName = "NewField";
    let counter = 1;
    while (newData[newFieldName]) {
      newFieldName = `NewField${counter}`;
      counter++;
    }

    newData[newFieldName] = "";
    // updateJsonString(newData, currentLang);
    setJsonData(newData);
    message.success("New root field has been added");

    // 滾動到底部
    setTimeout(() => {
      if (treeEditorRef.current) {
        treeEditorRef.current.scrollTo({
          top: treeEditorRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  // Add a nested object
  const handleAddNestedObject = () => {
    setJsonData((prevData) => {
      const newData = JSON.parse(JSON.stringify(prevData));
      let newFieldName = "NewObject";
      let counter = 1;
      // 用 hasOwnProperty 判斷 key 是否存在
      while (Object.prototype.hasOwnProperty.call(newData, newFieldName)) {
        newFieldName = `NewObject${counter}`;
        counter++;
      }
      newData[newFieldName] = { ChildField: "" };
      message.success("New nested object has been added");

      // 滾動到底部
      setTimeout(() => {
        if (treeEditorRef.current) {
          treeEditorRef.current.scrollTo({
            top: treeEditorRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 100);

      return newData;
    });
  };

  const handleFormSubmission = (jsonResult) => {
    // Prepare form data for API submission
    const formData = {
      files: {
        "zh_HK.json": currentLang === "zh" ? jsonResult : languages.zh.data,
        "zh_CN.json": currentLang === "cn" ? jsonResult : languages.cn.data,
        "en_US.json": currentLang === "en" ? jsonResult : languages.en.data,
        "ja_JP.json": currentLang === "ja" ? jsonResult : languages.ja.data,
        "ko_KR.json": currentLang === "ko" ? jsonResult : languages.ko.data,
      },
    };

    mutation.mutate(formData, {
      onSuccess: () => {
        message.success("JSON data has been saved successfully");
        // Update states
        setJsonData(jsonResult);
        setJsonString(JSON.stringify(jsonResult, null, 2));
        setLanguages((prevLangs) => ({
          ...prevLangs,
          [currentLang]: {
            ...prevLangs[currentLang],
            data: jsonResult,
          },
        }));
        // Update form values
        const baseFormValues = {
          json: JSON.stringify(jsonResult, null, 2),
        };
        const formValues = buildFormValues(jsonResult);
        form.setFieldsValue({
          ...baseFormValues,
          ...formValues,
        });
      },
      onError: () => {
        message.error("Failed to save JSON data");
      },
    });
  };

  // Add a new handleTreeFormSubmit function to process all form changes at once
  const handleTreeFormSubmit = (values) => {
    // Convert flat keys/values to nested JSON
    const result = {};

    // First, collect all key renames
    const keyMapping = {};
    Object.entries(values)
      .filter(([k]) => k.startsWith("keys."))
      .forEach(([k, v]) => {
        const path = k.replace(/^keys\./, "");
        keyMapping[path] = v;
      });

    // Process values and build the structure using the renamed keys
    Object.entries(values)
      .filter(([k]) => k.startsWith("values."))
      .forEach(([k, v]) => {
        const path = k.replace(/^values\./, "").split(".");

        // Build the path using renamed keys
        let currentPath = [];
        let curr = result;

        for (let i = 0; i < path.length; i++) {
          const pathSegment = path.slice(0, i + 1).join(".");
          const keyName = keyMapping[pathSegment] || path[i];

          if (i < path.length - 1) {
            // Create nested objects if needed
            curr[keyName] = curr[keyName] || {};
            curr = curr[keyName];
          } else {
            // Set the leaf value
            curr[keyName] = v;
          }

          currentPath.push(keyName);
        }
      });

    handleFormSubmission(result);
  };

  const handleJsonTextSubmit = (values) => {
    setLoading(true);
    let checkJson = values.json;
    if (!checkJson) {
      message.error("Please input JSON");
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(checkJson);
    } catch (error) {
      message.error("Invalid JSON format");
      return;
    }

    // Check each key
    const result = validateJsonKeys(parsed);
    if (!result.valid) {
      message.error(`Key "${result.path}" cannot start with a special character and should not be empty`);
      return;
    }

    handleFormSubmission(parsed);
  };

  // 檢查所有 key 是否符合規則
  const validateJsonKeys = (obj, path = []) => {
    // 第一個字不能是符號或空白
    const keyRegex = /^[A-Za-z0-9_\u4e00-\u9fa5]/; // 支援中英文、數字、底線
    for (const key in obj) {
      if (!keyRegex.test(key)) {
        return { valid: false, path: [...path, key].join(".") };
      }
      if (typeof obj[key] === "object" && obj[key] !== null) {
        const result = validateJsonKeys(obj[key], [...path, key]);
        if (!result.valid) return result;
      }
    }
    return { valid: true };
  };

  if (query.isPending) {
    return (
      <Card style={{ height: "100vh", width: "100%" }}>
        <Skeleton active />
      </Card>
    );
  }

  return (
    <Card style={{ width: "100%" }} title={<Title level={4}>PAA App 3.0 Translation</Title>}>
      <div style={{ marginBottom: 16 }}>
        <Space align="center">
          <Text strong>Change Language:</Text>
          <Radio.Group
            value={currentLang}
            onChange={(e) => {
              handleLanguageChange(e.target.value);
            }}
            style={{ marginLeft: 8 }}
          >
            {Object.keys(languages).map((lang) => (
              <Radio.Button key={lang} value={lang}>
                {languages[lang].name}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Space>
        <br />
        <Space style={{ color: "#6b7280" }}>(Before selecting a language, please save the modified text first.)</Space>
      </div>
      <Row gutter={[16, 16]}>
        <Col span={12} style={{ marginTop: "61px" }}>
          <Form layout="vertical">
            <div
              style={{
                maxHeight: "60vh",
                overflow: "auto",
                border: "1px solid #f0f0f0",
                padding: 16,
                marginBottom: 16,
              }}
            >
              {renderEngJsonForm(engJsonData)}
            </div>
          </Form>
        </Col>
        <Col span={12}>
          <Tabs
            activeKey={activeKey}
            onChange={(key) => {
              setActiveKey(key);
              if (key === "raw") {
                updateJsonString(jsonData, currentLang);
              }
            }}
            items={[
              {
                key: "tree",
                label: "Tree Editor",
                children: (
                  <Form
                    layout="vertical"
                    onFinish={(values) => {
                      setLoading(true);
                      handleTreeFormSubmit(values);
                    }}
                    form={form}
                  >
                    <div
                      ref={treeEditorRef}
                      style={{
                        maxHeight: "60vh",
                        overflow: "auto",
                        border: "1px solid #f0f0f0",
                        padding: 16,
                        marginBottom: 16,
                      }}
                    >
                      {renderJsonForm(jsonData)}
                    </div>

                    <Space style={{ width: "100%", justifyContent: "space-between" }}>
                      <Space>
                        <Button loading={loading} type="primary" icon={<PlusOutlined />} onClick={handleAddRootField}>
                          New Field
                        </Button>
                        <Button loading={loading} type="primary" icon={<PlusOutlined />} onClick={handleAddNestedObject}>
                          New Object
                        </Button>
                      </Space>
                      {/* <Button
                    loading={loading}
                    type="primary"
                    htmlType="submit"
                    size="large"
                    icon={<SaveOutlined />}
                  >
                    Save
                  </Button> */}
                      <Button
                        loading={loading}
                        type="primary"
                        size="large"
                        icon={<SaveOutlined />}
                        onClick={() => {
                          setLoading(true);
                          handleTreeFormSubmit(form.getFieldsValue());
                          query.refetch();
                          handleQueryData();
                        }}
                      >
                        Save
                      </Button>
                    </Space>
                  </Form>
                ),
              },
              {
                key: "raw",
                label: "Original JSON",
                children: (
                  <>
                    <Form onFinish={handleJsonTextSubmit} form={form}>
                      <Form.Item
                        name="json"
                        help={
                          <Text type="secondary">
                            Edit the raw JSON. When the format is correct, the changes will be reflected in the tree structure.
                          </Text>
                        }
                      >
                        <TextArea
                          rows={20}
                          value={jsonString}
                          // onChange={(e) => updateJsonData(e.target.value)}
                        />
                      </Form.Item>
                      <Space style={{ width: "100%", justifyContent: "flex-end" }}>
                        <Button
                          type="primary"
                          icon={<SaveOutlined />}
                          size="large"
                          loading={loading}
                          onClick={() => {
                            setLoading(true);
                            handleJsonTextSubmit(form.getFieldsValue());
                          }}
                        >
                          Save
                        </Button>
                      </Space>
                    </Form>
                  </>
                ),
              },
            ]}
          />
        </Col>
      </Row>
      <Divider />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text type="secondary">Current editing language: {languages[currentLang].name}</Text>
      </div>
    </Card>
  );
}

export default TranslationPage;
